import { isEqual } from "lodash";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { validateConfiguration } from "@app/arc";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { ProjectRunStatusChangePayload, ZipCodeAnalysisStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import { FilteredResource, Resource, ResourceType } from "@app/resources";
import { ColumnConfig, numberSort, printNumberAsString, SortDirections, WafflesFilterType, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { buildClassicShareState, buildReactorConfig, handleArchiveConfirm, handleHiddenChange, handlePublishProject, handleUpdateSummary, renderProjectStatus, toClassicActionMenuModel } from "@app/home/Helpers";
import { SharedProjectActionMenu } from "@app/projects/network-assessment/SharedProjectActionMenu";
import { AdequacyProgressBarLabels, ProgressBarBand } from "@app/shared/progressBarBand";
import { JSX } from "react";
import { formatDate } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { sortByDate } from "@app/shared/sortByDate";
import { ProgressBar } from "@app/shared/ProgressBar";
import ProjectTileData from "@app/projects/projectTileData";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import Roles from "@app/user/roles";
import { UserInfo, UserContextProps, withUserContext } from "@app/user/UserContext";
import { AccuracyBandAggregate } from "@app/projects/results/accuracy/accuracyBandAggregate";

interface ProjectLookup { [projectId: number]: Resource; }
interface ResultLookup { [projectId: number]: ProjectTileData; }

interface ProjectTableProps extends RouteComponentProps {
  dualMembershipToggles: Set<number>;
  filteredProjectResources: FilteredResource[];
  hasSpecialtyFilter: boolean;
  loading: boolean;
  onMemberSetChange: (projectId: number, showSupplementary: boolean) => void;
  projectResourceLookup: ProjectLookup;
  onArchived: (projectId: number) => void;
  onHidden: (projectId: number) => void;
  onUpdateProjectOptions: (summary: ProjectTileData) => void;
  setProjectIsReadOnly: (projectIds: number[]) => void;
  summaries: ResultLookup;
  user: UserInfo;
}

type ProjectTableCombinedProps = ProjectTableProps & NotificationContextProps & UserContextProps;

interface ListViewWaffleToppings {
  [wafflesKeyColumnName]: string;
  actionMenu: JSX.Element;
  projectTitle: JSX.Element;
  lastRunDate: string | JSX.Element;
  resultsFor: JSX.Element;
  adequacyTemplateName: string | JSX.Element;
  accuracyTemplateName: string | JSX.Element;
  counties: string | JSX.Element;
  adequacyIndex: string | JSX.Element;
  adequacyGraph: JSX.Element;
  accuracyIndex: string | JSX.Element;
  accuracyGraph: JSX.Element;
  gaps: string | JSX.Element;
  approvedExceptions: string | JSX.Element;
  totalExceptions: string | JSX.Element;
  membershipAtRisk: string | JSX.Element;
  totalMembership: string | JSX.Element;
  membershipAtRiskPercent: string | JSX.Element;
  attestationPercent: string | JSX.Element;
  unprocessedProviders: string | JSX.Element;
  unprocessedExceptions: string | JSX.Element;
  status: JSX.Element;
}
type TableState = "unloaded" | "loading" | "loaded";

interface ProjectTableState {
  columnConfig: ColumnConfig;
  records: ListViewWaffleToppings[];
  tableState: TableState;
}

interface RowInfo {
  validConfig: boolean;
}

type RowInfoCombined = FilteredResource & Resource & RowInfo & ProjectTileData;

class ProjectTable extends React.Component<ProjectTableCombinedProps, ProjectTableState> {
  private showDualMembership = false;
  constructor(props: ProjectTableCombinedProps) {
    super(props);
    this.state = {
      columnConfig: {},
      records: [],
      tableState: "unloaded",
    };
  }

  public componentDidMount() {
    if (Object.keys(this.props.projectResourceLookup).length > 0) {
      this.populateData();
    }
    this.props.subscribe(this.handleNotifications);
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public componentDidUpdate(prevProps: ProjectTableCombinedProps) {
    if ((!isEqual(prevProps.projectResourceLookup, this.props.projectResourceLookup) ||
      !isEqual(prevProps.summaries, this.props.summaries) ||
      !isEqual(prevProps.filteredProjectResources, this.props.filteredProjectResources) ||
      !isEqual(prevProps.dualMembershipToggles, this.props.dualMembershipToggles)) &&
      Object.keys(this.props.summaries).length > 0) {
      this.populateData();

      if (prevProps.loading && !this.props.loading) {
        this.setState({
          tableState: "loading",
        }, () => {
          this.setState({
            tableState: "loaded",
          });
        })
      }
    }
  }

  public render() {
    if (this.state.tableState !== "loading") {
      return (
        //rendering table initially and again when data is loaded so columns dynamically resize
        <Waffles<ListViewWaffleToppings>
          columns={this.state.columnConfig}
          customSort={this.customSort}
          keyColumns={["projectTitle"]}
          data={this.state.records}
          fullWidth={true}
          testId="list-view"
        />
      );
    } else {
      return <LoadingSpinner />;
    }
  }

  private buildWaffleData = (records: RowInfoCombined[]) => {
    const shimmer = this.props.loading && (<div className="skeleton"><div className="bone" /></div>);
    /* oxlint-disable sort-keys */
    return records.map<ListViewWaffleToppings>((sr) => {
      const filteredProjectResource = { ...sr } as FilteredResource;
      const projectResource = { ...sr } as Resource;
      const isZipProject = projectResource.resourceType === ResourceType.ZipAnalysisProject;
      const projectTypeClassName = isZipProject ? "qes-zip-project" : "qes-county-project";
      return {
        [wafflesKeyColumnName]: sr.resourceId.toString(),
        actionMenu:
          (
            <div style={{ overflow: "visible", width: "0px" }} data-overflow={true}>
              <SharedProjectActionMenu
                projectOwned={!sr.isShared}
                model={toClassicActionMenuModel(projectResource, { ...sr } as ProjectTileData)}
                callbacks={{
                  onArchiveConfirm: () => handleArchiveConfirm(projectResource.isArchived || null, projectResource.resourceId, this.props.onArchived),
                  onPublish: (comment) => handlePublishProject(projectResource.resourceId, isZipProject, comment, () => handleUpdateSummary(this.props.onUpdateProjectOptions, projectResource, filteredProjectResource)),
                  onUpdateProjectOptions: () => handleUpdateSummary(this.props.onUpdateProjectOptions, projectResource, filteredProjectResource),
                }}
                shareLocationState={buildClassicShareState(projectResource, this.props.location.pathname)}
                user={this.props.user}
                setProjectIsReadOnly={this.props.setProjectIsReadOnly}
                showingHidden={projectResource.isHidden}
                onHidden={() => handleHiddenChange(projectResource.resourceId, this.props.onHidden)}
                fixMenu={true}
              />
            </div>
          ),
        projectTitle: (() => {
          const startingModule = !isZipProject && !!sr.accuracy ? "accuracy" : "adequacy";
          let tileUrl = `/projects/${sr.resourceId}/${startingModule}`;

          if (isZipProject && sr.specialtyGroupCodes?.length === 1) {
            tileUrl += `/specialty/${sr.specialtyGroupCodes[0]}`;
          } else if (!isZipProject && !!sr.filterPath) {
            tileUrl += sr.filterPath;
          }

          return (
            <div className={`position-relative ps-3 ${projectTypeClassName}`} key={sr.name.toLowerCase()}>
              <span title={sr.name}>
                <Link to={{ pathname: tileUrl }}>
                  {sr.name}
                </Link>
              </span>
            </div>
          );
        })(),
        lastRunDate: shimmer || ((sr.validConfig || sr.accuracy || isZipProject) && !this.nullOrUndefined(sr.lastModifiedDate) ? formatDate(sr.lastModifiedDate) : (sr.validConfig || sr.accuracy) && !this.nullOrUndefined(sr.dateCreated) ? formatDate(sr.dateCreated) : "---"),
        resultsFor: shimmer || (() => {
          if (!sr.isDualMembership || isZipProject) {
            return null;
          }
          const selected = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionMemberSetAlias : sr.memberSetAlias;
          const dropdownStyle = { width: 200 };
          return (
            <div style={dropdownStyle}>
              <QESSelect
                label="Selected Membership"
                hideLabel={true}
                testId={`project-table__select-membership--${sr.projectId}`}
                className="form-control"
                onChange={() => this.props.onMemberSetChange(sr.projectId, !this.props.dualMembershipToggles.has(sr.projectId))}
                value={selected}
              >
                <option value={sr.memberSetAlias}>{sr.memberSetAlias}</option>
                <option value={sr.companionMemberSetAlias}>{sr.companionMemberSetAlias}</option>
              </QESSelect>
            </div>
          );
        })(),
        adequacyTemplateName: shimmer || (sr.adequacyTemplateName || "---"),
        accuracyTemplateName: shimmer || (sr.accuracyTemplateName || "---"),
        counties: shimmer || this.renderCountyCount(sr, isZipProject),
        adequacyIndex: shimmer || (() => {
          const adequacyIndex = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionAdequacy && sr.companionAdequacy.adequacyIndex : sr.adequacy && sr.adequacy.adequacyIndex;
          return sr.validConfig && sr.adequacy && !this.nullOrUndefined(adequacyIndex) && !isZipProject ? adequacyIndex.toFixed(1) : "---";
        })(),
        adequacyGraph: shimmer || (() => {
          const graphObj = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionAdequacy : sr.adequacy;
          return (
            <div className="w-200">
              {sr.validConfig && graphObj && !isZipProject ?
                <ProgressBar
                  bands={[
                    {
                      color: "progress-bar-success",
                      colorType: "class",
                      label: AdequacyProgressBarLabels.MetBothCriteria,
                      outOfScale: false,
                      percent: graphObj.percentPassBoth,
                    },
                    {
                      color: "progress-bar-warning",
                      colorType: "class",
                      label: AdequacyProgressBarLabels.MetOneCriteria,
                      outOfScale: false,
                      percent: graphObj.percentPassAccessOnly + graphObj.percentPassServicingOnly,
                    },
                    {
                      color: "progress-bar-danger",
                      colorType: "class",
                      label: AdequacyProgressBarLabels.MetNoCriteria,
                      outOfScale: false,
                      percent: graphObj.percentPassNone,
                    },
                    {
                      color: "progress-bar-info",
                      colorType: "class",
                      label: AdequacyProgressBarLabels.ApprovedException,
                      outOfScale: false,
                      percent: graphObj.percentPassByException,
                    },
                  ]}
                  isGrayscale={false}
                /> :
                <ProgressBar
                  bands={[]}
                  isGrayscale={false}
                  isMuted={true}
                />
              }
            </div>
          );
        })(),
        accuracyIndex: shimmer || (() => {
          return sr.accuracy && !this.nullOrUndefined(sr.accuracy.accuracyIndex) && !isZipProject ? sr.accuracy.accuracyIndex.toString() : "---";
        })(),
        accuracyGraph: shimmer || (() => {
          return (
            <div className="w-200">
              {sr.accuracy && !isZipProject ?
                <ProgressBar
                  bands={this.getAccuracyProgressBarBands(sr.accuracy.bands)}
                  isGrayscale={false}
                /> :
                <ProgressBar
                  bands={[]}
                  isGrayscale={false}
                  isMuted={true}
                />
              }
            </div>
          );
        })(),
        gaps: shimmer || (() => {
          const gaps = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionAdequacy && sr.companionAdequacy.gapCount : sr.adequacy && sr.adequacy.gapCount;
          return sr.validConfig && !this.nullOrUndefined(gaps) && !isZipProject ? gaps.toLocaleString("en-US") : "---";
        })(),
        totalExceptions: shimmer || (() => {
          const exceptions = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionAdequacy && sr.companionAdequacy.exceptionCount : sr.adequacy && sr.adequacy.exceptionCount;
          return sr.validConfig && !this.nullOrUndefined(exceptions) && !isZipProject ? exceptions.toLocaleString("en-US") : "---";
        })(),
        approvedExceptions: shimmer || (() => {
          const exceptions = this.props.dualMembershipToggles.has(sr.projectId) ? sr.companionAdequacy && sr.companionAdequacy.approvedExceptionCount : sr.adequacy && sr.adequacy.approvedExceptionCount;
          return sr.validConfig && !this.nullOrUndefined(exceptions) && !isZipProject ? exceptions.toLocaleString("en-US") : "---";
        })(),
        membershipAtRisk: shimmer || (!this.props.hasSpecialtyFilter && sr.adequacy && !isZipProject ? printNumberAsString(sr.adequacy.membershipRiskCount, "---") : "---"),
        totalMembership: shimmer || (!this.props.hasSpecialtyFilter && sr.adequacy && !isZipProject ? printNumberAsString(sr.adequacy.membershipRiskServiceAreaCount, "---") : "---"),
        membershipAtRiskPercent: shimmer || (!this.props.hasSpecialtyFilter && sr.adequacy && !isNaN(sr.adequacy.percentMembershipRisk) && !isZipProject ? (sr.adequacy.percentMembershipRisk).toFixed(1) : "---"),
        attestationPercent: shimmer || this.renderAttestationPercent(sr, isZipProject),
        unprocessedProviders: shimmer || (sr.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed &&
          (this.props.user.roles.includes(Roles.PROJECTION_PROCESSOR) ||
            this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN)) &&
          !sr.isRunning && !isZipProject ?
          sr.hasUnprocessedProjectionProviders ||
            sr.hasUnprocessedProviderTerminations ? "Y" : "N" : "---"),
        unprocessedExceptions: shimmer || (sr.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed &&
          this.props.user.roles.includes(Roles.EXCEPTION_MANAGER) && !sr.isRunning && !isZipProject ?
          sr.hasUnprocessedSpecialtyGroupExceptions ? "Y" : "N" : "---"),
        status: shimmer || renderProjectStatus(this.props.summaries[sr.projectId]),
      };
    });
    /* oxlint-enable sort-keys */
  }

  private getAccuracyProgressBarBands(resultBands: AccuracyBandAggregate[]) {
    const progressBarBands: ProgressBarBand[] = [];

    if (resultBands) {
      const total: number = resultBands.reduce((sum, band) => sum + (band.inspectionCount * band.weightSummation), 0);

      for (const resultBand of resultBands) {
        const value: number = resultBand.inspectionCount * resultBand.weightSummation;
        const percent: number = resultBand ? (value / total) : 0;

        const progressBarBand: ProgressBarBand = {
          color: resultBand.color,
          colorType: "hex",
          outOfScale: false,
          percent: percent * 100,
        };

        progressBarBands.push(progressBarBand);
      }
    }

    return progressBarBands;
  }

  private populateData = () => {
    const records: RowInfoCombined[] = [];
    this.props.filteredProjectResources.forEach((filteredResource: FilteredResource) => {
      const resource = this.props.projectResourceLookup[filteredResource.resourceId];
      if (resource) {
        const projectTileData = this.props.summaries[resource.resourceId];
        const reactorConfig = buildReactorConfig(projectTileData); //using the reactor config to determine if the rows should display data or placeholders
        //using the reactor configuration may seem odd without a reactor, but it will ensure consistency with the project tile
        const validConfig = (projectTileData?.resource?.resourceType === ResourceType.ZipAnalysisProject ||
          validateConfiguration(reactorConfig).isRight());

        const rowInfo = { validConfig } as RowInfo;

        records.push(Object.assign(rowInfo, filteredResource, resource, projectTileData));
      }
    });
    this.showDualMembership = records.some((record) => record.isDualMembership);
    this.setState({
      columnConfig: this.getColumnConfig(records),
      records: this.buildWaffleData(records.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        if (nameA < nameB) { return -1; }
        if (nameA > nameB) { return 1; }
        return 0;
      })),
    });
  }

  private nullOrUndefined = (object: unknown) => {
    return object === undefined || object === null;
  }

  private handleNotifications = async (message: Message) => {
    if (message) {
      switch (message.notificationType as MessageType) {
        case MessageType.ProcessingProjectionAnalysis:
        case MessageType.ProcessingSpecialtyGroupExceptions:
        case MessageType.ProjectRunStatusChange:
        case MessageType.ProcessingProviderChanges:
        case MessageType.ProcessingProspectMarketNcp:
          const projectRunStatusChangePayload = message.payload as ProjectRunStatusChangePayload;
          if (Object.keys(this.props.summaries).length > 0 &&
            Object.keys(this.props.projectResourceLookup).length > 0 &&
            this.props.filteredProjectResources.length > 0 &&
            this.props.projectResourceLookup[projectRunStatusChangePayload.projectId] &&
            this.props.filteredProjectResources.find((fpr) => fpr.resourceId === projectRunStatusChangePayload.projectId) &&
            this.props.summaries[projectRunStatusChangePayload.projectId] &&
            (!this.props.summaries[projectRunStatusChangePayload.projectId].resource.isShared || projectRunStatusChangePayload.isPublished || this.props.user.roles.indexOf(Roles.PREVIEW) > -1) &&
            (this.props.summaries[projectRunStatusChangePayload.projectId].runConfigurationStatus !== projectRunStatusChangePayload.status || projectRunStatusChangePayload.status === ProjectRunConfigurationStatus.Succeeded)) {

            await handleUpdateSummary(this.props.onUpdateProjectOptions, this.props.projectResourceLookup[projectRunStatusChangePayload.projectId], this.props.filteredProjectResources.find((fpr) => fpr.resourceId === projectRunStatusChangePayload.projectId));
          }
          break;
        case MessageType.ZipCodeAnalysisStatusChangeNotification:
          const zipPayload = message.payload as ZipCodeAnalysisStatusChangeNotificationMessagePayload;
          const summary = this.props.summaries[zipPayload.projectId];
          if (Object.keys(this.props.summaries).length > 0 &&
            Object.keys(this.props.projectResourceLookup).length > 0 &&
            this.props.filteredProjectResources.length > 0 &&
            summary &&
            summary.projectId === zipPayload.projectId &&
            (!summary.resource.isShared || this.props.user.roles.indexOf(Roles.PREVIEW) > -1) &&
            (summary.runConfigurationStatus !== zipPayload.projectRunStatus || zipPayload.projectRunStatus === ProjectRunConfigurationStatus.Succeeded)) {
            await handleUpdateSummary(this.props.onUpdateProjectOptions, this.props.projectResourceLookup[zipPayload.projectId], this.props.filteredProjectResources.find((fpr) => fpr.resourceId === zipPayload.projectId));
          }
          break;
      }
    }
  }

  private getColumnConfig = (records: RowInfoCombined[]) => {
    const hideAttestation = records.every((record) => !record.showAttestation);
    const hideAccuracy = records.every((record) => !record.accuracy);
    const config: ColumnConfig = {
      accuracyGraph: {
        displayText: "Accuracy",
        hidden: hideAccuracy,
        noFilter: true,
        style: {
          verticalAlign: "middle",
        },
        testId: "accuracy-graph",
      },
      accuracyIndex: {
        displayText: "Accuracy Index",
        filterType: WafflesFilterType.number,
        hidden: hideAccuracy,
        style: {
          verticalAlign: "middle",
        },
        testId: "accuracy-index",
      },
      accuracyTemplateName: {
        displayText: "Accuracy Template Name",
        filterType: WafflesFilterType.string,
        style: {
          verticalAlign: "middle",
        },
        testId: "accuracy-template-name",
      },
      actionMenu: {
        displayText: "",
        noHorizontalPadding: true,
        noResize: true,
        noSort: true,
        sticky: true,
        style: {
          verticalAlign: "middle",
          // this needs to be higher than the next column for the project action menu to render
          // and sticky columns are 10 by default
          zIndex: 11,
        },
        testId: "action-menu",
      },
      adequacyGraph: {
        displayText: "Adequacy",
        minWidth: "200px",
        noFilter: true,
        style: {
          verticalAlign: "middle",
        },
        testId: "adequacy-graph",
      },
      adequacyIndex: {
        displayText: "Adequacy Index",
        filterType: WafflesFilterType.number,
        style: {
          verticalAlign: "middle",
        },
        testId: "adequacy-index",
      },
      adequacyTemplateName: {
        displayText: "Adequacy Template Name",
        filterType: WafflesFilterType.string,
        style: {
          verticalAlign: "middle",
        },
        testId: "adequacy-template-name",
      },
      approvedExceptions: {
        displayText: "Approved Exceptions",
        filterType: WafflesFilterType.number,
        style: {
          verticalAlign: "middle",
        },
        testId: "approved-exceptions",
      },
      attestationPercent: {
        displayText: "Attestation %",
        filterType: WafflesFilterType.number,
        hidden: hideAttestation,
        style: {
          verticalAlign: "middle",
        },
        testId: "attestation-percent",
      },
      counties: {
        displayText: "Counties",
        filterType: WafflesFilterType.number,
        style: {
          verticalAlign: "middle",
        },
        testId: "counties",
      },
      gaps: {
        displayText: "Gaps",
        filterType: WafflesFilterType.number,
        style: {
          verticalAlign: "middle",
        },
        testId: "gaps",
      },
      lastRunDate: {
        displayText: "Last Run On",
        style: {
          verticalAlign: "middle",
        },
        testId: "last-run-on",
      },
      membershipAtRisk: {
        displayText: "Membership At Risk #",
        filterType: WafflesFilterType.number,
        hidden: !this.props.user.roles.includes(Roles.MEMBERSHIP_RISK_USER),
        style: {
          verticalAlign: "middle",
        },
        testId: "membership-risk",
      },
      membershipAtRiskPercent: {
        displayText: "Membership At Risk %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.user.roles.includes(Roles.MEMBERSHIP_RISK_USER),
        style: {
          verticalAlign: "middle",
        },
        testId: "membership-risk-percent",
      },
      projectTitle: {
        displayText: "Project Title",
        filterType: WafflesFilterType.string,
        sticky: true,
        style: {
          verticalAlign: "middle",
        },
        testId: "title",
      },
      resultsFor: {
        displayText: "Results For",
        filterType: WafflesFilterType.boolean,
        hidden: !this.showDualMembership,
        style: {
          verticalAlign: "middle",
        },
        testId: "results-for",
      },
      status: {
        displayText: "Status",
        noFilter: true,
        noSort: true,
        style: {
          verticalAlign: "middle",
        },
        testId: "status",
      },
      totalExceptions: {
        displayText: "Total Exceptions",
        filterType: WafflesFilterType.number,
        style: {
          verticalAlign: "middle",
        },
        testId: "total-exceptions",
      },
      totalMembership: {
        displayText: "Total Membership #",
        filterType: WafflesFilterType.number,
        hidden: !this.props.user.roles.includes(Roles.MEMBERSHIP_RISK_USER),
        style: {
          verticalAlign: "middle",
        },
        testId: "total-membership",
      },
      unprocessedExceptions: {
        displayText: "Unprocessed Exceptions",
        hidden: !this.props.user.roles.includes(Roles.EXCEPTION_MANAGER),
        style: {
          verticalAlign: "middle",
        },
        testId: "unprocessed-exceptions",
      },
      unprocessedProviders: {
        displayText: "Unprocessed Providers",
        hidden: !this.props.user.roles.includes(Roles.PROJECTION_PROCESSOR) && !this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN),
        style: {
          verticalAlign: "middle",
        },
        testId: "unprocessed-providers",
      },
    };
    return config;
  }

  private customSort = (records: ListViewWaffleToppings[], column: keyof ListViewWaffleToppings, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "unprocessedExceptions":
      case "accuracyTemplateName":
      case "adequacyTemplateName":
      case "unprocessedProviders":
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "counties":
      case "gaps":
      case "approvedExceptions":
      case "totalExceptions":
      case "membershipAtRisk":
      case "membershipAtRiskPercent":
      case "totalMembership":
        return numberSort<ListViewWaffleToppings>(records, column, multiplier);
      case "attestationPercent":
        return numberSort<ListViewWaffleToppings>(records, "attestationPercent", multiplier);
      case "projectTitle":
        return data.sort((a, b) => (a.projectTitle.key > b.projectTitle.key ? 1 : -1) * multiplier);
      case "adequacyIndex":
      case "adequacyGraph":
        return numberSort<ListViewWaffleToppings>(records, "adequacyIndex", multiplier);
      case "resultsFor":
        const withResultsFor: ListViewWaffleToppings[] = [];
        const withoutResultsFor: ListViewWaffleToppings[] = [];
        data.forEach((d) => {
          if (!!d.resultsFor) {
            withResultsFor.push(d);
          } else {
            withoutResultsFor.push(d);
          }
        });
        return multiplier === 1 ? withoutResultsFor.concat(withResultsFor) : withResultsFor.concat(withoutResultsFor);
      case "accuracyIndex":
      case "accuracyGraph":
        return numberSort<ListViewWaffleToppings>(records, "accuracyIndex", multiplier);
      case "lastRunDate":
        return sortByDate(data, "lastRunDate", direction, "projectTitle", "key");
      default:
        return data;
    }
  }

  private renderAttestationPercent = (projectData: RowInfoCombined, isZipProject: boolean) => {
    if (!projectData.showAttestation || isZipProject) return "---";

    let attestedPercent = "---";
    if (projectData.adequacy) {
      if (projectData.validConfig) {
        if (this.props.dualMembershipToggles.has(projectData.projectId) && projectData.companionAdequacy) {
          attestedPercent = typeof projectData.companionAdequacy.recentlyAttestedUniqueNPIPercent !== "undefined" ?
            (projectData.companionAdequacy.recentlyAttestedUniqueNPIPercent ?? 0).toString() : "0";
        } else {
          attestedPercent = typeof projectData.adequacy.recentlyAttestedUniqueNPIPercent !== "undefined" ?
            (projectData.adequacy.recentlyAttestedUniqueNPIPercent ?? 0).toString() : "0";
        }
      }
    }

    if (projectData.accuracy) {
      attestedPercent = typeof projectData.accuracy.recentlyAttestedPercent !== "undefined" ?
        (projectData.accuracy.recentlyAttestedPercent ?? 0).toString() : "0";
    }

    return attestedPercent != "0" ? attestedPercent : "---";
  }

  private renderCountyCount = (projectData: RowInfoCombined, isZipProject: boolean) => {

    if (isZipProject) return "---";

    let countyCount = "---";
    if (projectData.adequacy) {
      if (projectData.validConfig) {
        countyCount = typeof projectData.adequacy.countyCount !== "undefined" ?
          (projectData.adequacy.countyCount ?? 0).toLocaleString("en-US") : "0";
      }
      return countyCount != "0" ? countyCount : "---";
    }

    if (projectData.accuracy) {
      countyCount = typeof projectData.accuracyCountyCount !== "undefined" ?
        (projectData.accuracyCountyCount ?? 0).toLocaleString("en-US") : "0";
    }
    return countyCount != "0" ? countyCount : "---";
  }

}

const WrappedProjectTable = withUserContext(withNotificationContext(ProjectTable));

export default WrappedProjectTable as React.ComponentClass<ProjectTableProps>;
